import styles from './TextInputShared.module.scss';

import {
  type Ref,
  type ReactNode,
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { InputContainer, type InputAlign } from './InputContainer';
import clsx from 'clsx';
import { FormContext } from '../form/FormContext';
import { FormFieldInputContext } from '../form';
import { useMergeRefs } from '@libs/react/ref';

export type TextInputType = 'text' | 'email' | 'password' | 'number';

interface Props {
  addon?: ReactNode;
  align?: InputAlign;
  bottom?: ReactNode;
  defaultValue?: string;
  disabled?: boolean;
  end?: ReactNode;
  id?: string;
  label: string;
  max?: number;
  maxLength?: number;
  min?: number;
  name?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onValue?: (value: string) => void;
  prefix?: string;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  start?: ReactNode;
  step?: number;
  suffix?: string;
  type?: TextInputType;
  value?: string;
}

export function TextInputShared(props: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRefs(props.ref, ref);
  const align: InputAlign = props.align ?? 'start';
  const className = clsx(styles['content'], styles[`align-${align}`]);
  const inputClassName = clsx(styles['input']);
  const inputType = props.type ?? 'text';
  const formContext = useContext(FormContext);
  // const formFieldContext = useContext(FormFieldContext);
  const formFieldInputContext = useContext(FormFieldInputContext);
  const name = props.name;
  // const name = props.name ?? formFieldContext.name;

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    props.onChange?.(event);
    props.onValue?.(event.currentTarget.value);
  }

  function onClick() {
    if (ref.current) {
      ref.current.focus();
    }
  }

  const prefix = props.prefix && (
    <div className={styles['prefix']}>{props.prefix}</div>
  );
  const suffix = props.suffix && (
    <div className={styles['suffix']}>{props.suffix}</div>
  );

  useEffect(() => {
    if (name && ref.current) {
      const prev = formContext.inputs.get(name) ?? [];
      formContext.inputs.set(name, [...prev, ref.current]);
      formFieldInputContext?.set(name, ref.current);
      return () => {
        formContext.inputs.delete(name);
      };
    }
  }, [formContext, name, formFieldInputContext]);

  return (
    <InputContainer
      addon={props.addon}
      end={props.end}
      id={props.id}
      label={props.label}
      onClick={onClick}
      start={props.start}
    >
      {({ id }) => {
        return (
          <div className={className}>
            {prefix}
            <input
              ref={mergedRef}
              className={inputClassName}
              defaultValue={props.defaultValue}
              disabled={props.disabled}
              id={id}
              max={props.max}
              maxLength={props.maxLength}
              min={props.min}
              name={name}
              onBlur={props.onBlur}
              onChange={onChange}
              onFocus={props.onFocus}
              onKeyDown={props.onKeyDown}
              required={props.required}
              step={props.step}
              type={inputType}
              value={props.value}
            />
            {suffix}
          </div>
        );
      }}
    </InputContainer>
  );
}
